/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseCrudService, API_CONFIG, FilterSearchByTermService } from '@nebula-shared/data-access';
export interface CreateFormatoContablePayload {
  codigo: string;
  nombre: string;
  tipoOperacionConceptoId?: number;
  generaAsientoOrden?: string;
  cuentaDebitoOrdenId?: number | null;
  cuentaCreditoOrdenId?: number | null;
  cuentasContables: CreateCuentaContablePayload[];
  cuentasRetenedoras: CreateCuentaRetenedoraPayload[];
  distribucionCc: CreateDistribucionCcPayload[];
}

export interface CreateCuentaContablePayload {
  tipoCentroCostoId?: number | null;
  cuentaDebitoId: number;
  cuentaCreditoId: number;
  cuentaIvaId?: number | null;
  cuentaAmortizacionAnticipoId?: number | null;
}

export interface CreateCuentaRetenedoraPayload {
  cuentaRetencionId: number;
  retencionRentasTrabajo?: string;
  porcentajeRetencion: number;
  terceroId?: number | null;
}

export interface CreateDistribucionCcPayload {
  tipoCentroCostoId: number;
  centroCostoId: number;
  porcentaje: number;
}

export interface UpdateFormatoContablePayload {
  id: number;
  codigo: string;
  nombre: string;
  tipoOperacionConceptoId: number;
  generaAsientoOrden?: string;
  cuentaDebitoOrdenId?: number | null;
  cuentaCreditoOrdenId?: number | null;
  cuentasContables: UpdateCuentaContablePayload[];
  cuentasRetenedoras: UpdateCuentaRetenedoraPayload[];
  distribucionCc: UpdateDistribucionCcPayload[];
  version: number;
}

export interface UpdateCuentaContablePayload {
  id?: number;
  tipoCentroCostoId?: number | null;
  cuentaDebitoId: number;
  cuentaCreditoId: number;
  cuentaIvaId?: number | null;
  cuentaAmortizacionAnticipoId?: number | null;
  status?: string;
}

export interface UpdateCuentaRetenedoraPayload {
  id?: number;
  cuentaRetencionId: number;
  retencionRentasTrabajo?: string;
  porcentajeRetencion: number;
  terceroId?: number | null;
  status?: string;
}

export interface UpdateDistribucionCcPayload {
  id?: number;
  tipoCentroCostoId: number;
  centroCostoId: number;
  porcentaje: number;
  status?: string;
}

export interface ErrorDetail {
  code: string;
  field: string;
  message: string;
}

export interface SuccessResponse<T> {
  timestamp: string;
  status: number;
  success: true;
  data: T;
  message: null | string;
  errors: [];
}

export interface ErrorResponse {
  timestamp: string;
  status: number;
  success: false;
  data: null;
  message: string;
  errors: ErrorDetail[];
}

export type ApiResponse<T> = SuccessResponse<T> | ErrorResponse;

export interface SimappeError {
  status?: number;
  message?: string;
  errors?: Array<{ code: string; field: string; message: string }>;
  solutions?: string[];
}

export interface CuentaContableDto {
  id?: number;
  formatoId?: number;
  tipoCentroCostoId?: number | null;
  tipoCentroCostoNombre?: string;
  tipoCentroCostoCodigo?: string;
  cuentaDebitoId: number;
  cuentaDebitoCodigo?: string;
  cuentaDebitoNombre?: string;
  cuentaCreditoId: number;
  cuentaCreditoCodigo?: string;
  cuentaCreditoNombre?: string;
  cuentaIvaId?: number | string | null;
  cuentaIvaCodigo?: string;
  cuentaIvaNombre?: string;
  cuentaAmortizacionAnticipoId?: number | null;

  codigoBancoDebito?: string | null;
  codigoBancoDebitoId?: number | null;
  codigoBancoCredito?: string;
  codigoBancoCreditoId?: number | null;

  cuentaAmortizacionAnticipoCodigo?: string;
  cuentaAmortizacionAnticipoNombre?: string;
  status?: string;
  clientId?: number;
  companyId?: number;
  version?: number;
}

export interface CuentaRetenedoraDto {
  id?: number;
  formatoId?: number;
  cuentaRetencionId: number;
  cuentaRetencionCodigo?: string;
  cuentaRetencionNombre?: string;
  retencionRentasTrabajo?: string;
  porcentajeRetencion: number | null;
  terceroId?: number | null;
  terceroNumeroDocumento?: string;
  terceroNombreCompleto?: string;
  status?: string;
  createdBy?: string;
  updatedBy?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  version?: number;
  clientId?: number;
  companyId?: number;
  subsidiaryId?: number | null;
}

export interface DistribucionCcDto {
  id?: number;
  formatoId?: number;

  tipoCentroCostoId: number;
  tipoCentroCostoNombre?: string;
  centroCostoId: number;
  centroCostoCodigo?: string;
  centroCostoNombre?: string;
  /**
   * Estado de negocio del centro de la distribucion (R-E02): ACTIVO o INACTIVO; nulo si el centro
   * ya no existe en el tenant o esta ELIMINADO. Lo trae el servidor desde accounting-core 2.27.51.
   */
  estadoCentroCostos?: 'ACTIVO' | 'INACTIVO' | null;

  porcentaje: number;

  status?: string;
  version?: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  clientId?: number;
  companyId?: number;
  subsidiaryId?: number | null;
}

export interface PageResponseDistribucionCc {
  content: DistribucionCcDto[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  empty: boolean;
}

export interface CreateDistribucionCcPayload {
  formatoId: number;
  tipoCentroCostoId: number;
  centroCostoId: number;
  porcentaje: number;
}

export interface DistribucionCcEstadistica {
  totalDistribuciones: number;
  sumaPorcentajes: number;
  isValidPorcentajes: boolean;
  distribucionPorTipo: {
    tipoCentroCostoId: number;
    tipoCentroCostoNombre: string;
    cantidad: number;
    sumaPorcentaje: number;
  }[];
}

export interface DistribucionCcValidacion {
  isValid: boolean;
  sumaPorcentajes: number;
  mensaje?: string;
  distribucionesInvalidas?: DistribucionCcDto[];
}

export interface CentroCostoSeleccionado {
  id: number;
  codigo: string;
  nombre: string;
  tipoCentroCostoId: number;
  tipoCentroCostoNombre?: string;
}

export enum TipoCentroCosto {
  ADMINISTRATIVO = 1,
  OPERATIVO = 2,
  VENTAS = 3,
  DISTRIBUCION = 4,
}

export const TIPO_CENTRO_COSTO_LABELS: Record<number, string> = {
  [TipoCentroCosto.ADMINISTRATIVO]: 'ADMINISTRATIVO',
  [TipoCentroCosto.OPERATIVO]: 'OPERATIVO',
  [TipoCentroCosto.VENTAS]: 'VENTAS',
  [TipoCentroCosto.DISTRIBUCION]: 'DISTRIBUCIÓN',
};

export const TIPO_CENTRO_COSTO_OPTIONS = [
  { label: 'ADMINISTRATIVO', value: TipoCentroCosto.ADMINISTRATIVO },
  { label: 'OPERATIVO', value: TipoCentroCosto.OPERATIVO },
  { label: 'VENTAS', value: TipoCentroCosto.VENTAS },
  { label: 'DISTRIBUCIÓN', value: TipoCentroCosto.DISTRIBUCION },
];

export interface DistribucionErrorDetail {
  code: string;
  field: string;
  message: string;
}

export interface DistribucionSimappeError {
  status?: number;
  message?: string;
  errors?: DistribucionErrorDetail[];
  solutions?: string[];
}

export interface PageResponseDistribucionCc {
  content: DistribucionCcDto[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface FormatoContableDto {
  id?: number;
  codigo: string;
  nombre: string;
  tipoOperacionConceptoId: number;
  tipoOperacionCodigo?: string;
  tipoOperacionNombre?: string;
  generaAsientoOrden?: string;
  cuentaDebitoOrdenId?: number | null;
  cuentaCreditoOrdenId?: number | null;
  cuentasContables: CuentaContableDto[];
  cuentasRetenedoras: CuentaRetenedoraDto[];
  distribucionCc: DistribucionCcDto[];
  status?: string;
  clientId?: number;
  companyId?: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  version?: number;
}

export interface FormatoContableResponse {
  id: number;
  codigo: string;
  nombre: string;
  tipoOperacionConceptoId: number;
  tipoOperacionNombre: string;
  generaAsientoOrden?: string;
  status: string;
  estadoFormato: string;
}

export interface PageResponseFormatoContable {
  content: FormatoContableResponse[];
  size: number;
  number: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
  empty: boolean;
  pageNumber?: number;
  pageSize?: number;
  first?: boolean;
  last?: boolean;
}

export interface PageResponseFormatoContableDto {
  content: FormatoContableDto[];
  size: number;
  number: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
  empty: boolean;
}

export interface AplicarFormatoRequest {
  formatoContableId: number;
  terceroId: number;
  valorBase: number;
  valorImpuesto: number;
  valorAmortizar: number;
  tipoCentroCostoId?: number;
  centroCostosId?: number;
  distribucionesCc?: DistribucionAplicacionRequest[];
}

export interface AplicarFormatoAsientoRequest {
  formatoContable: string;
  tipoCentroCostosId?: number;
  codigoCentroCosto?: string;
  valorAntesImpuesto: number;
  valorImpuesto: number;
  valorAmortizar: number;
  terceroId: number;
  conceptoContableId: number;
  fecha: string;
}

export interface DistribucionAplicacionRequest {
  tipoCentroCostoId: number;
  centroCostoId: number;
  porcentaje: number;
}

export interface LineaAsientoContable {
  id?: number;
  cuentaContableId: number;
  cuentaContableCodigo: string;
  cuentaContableNombre: string;
  centroCostoId?: number;
  centroCostoNombre?: string;
  naturaleza: 'DEBITO' | 'CREDITO';
  valor: number;
  base?: number;
}

export interface AplicarFormatoResponse {
  lineasAsiento: LineaAsientoContable[];
  totalDebito: number;
  totalCredito: number;
  estaCuadrado: boolean;
}

@Injectable({ providedIn: 'root' })
export class FormatoContableService extends BaseCrudService<
  FormatoContableDto,
  CreateFormatoContablePayload,
  UpdateFormatoContablePayload
> {
  private filterService = inject(FilterSearchByTermService);

  private searchFields = ['codigo', 'nombre'];

  constructor() {
    super();
    const config = API_CONFIG.accounting;
    this.setBaseUrl(`${config.baseUrl}${config.endpoints.formatosContables}`);
  }

  override getAll(): Observable<FormatoContableDto[]> {
    return this.http.getContent<FormatoContableDto[]>(`${this.baseUrl}/read`);
  }

  override getById(id: number): Observable<FormatoContableDto> {
    return this.http.getContent<FormatoContableDto>(`${this.baseUrl}/get`, { id: String(id) });
  }

  override create(payload: CreateFormatoContablePayload): Observable<FormatoContableDto> {
    return this.http
      .post<FormatoContableDto>(`${this.baseUrl}/create`, payload)
      .pipe(map((response: any) => this.extractData(response)));
  }

  override update(
    id: number,
    payload: UpdateFormatoContablePayload,
  ): Observable<FormatoContableDto> {
    return this.http
      .put<FormatoContableDto>(`${this.baseUrl}/update?id=${id}`, payload)
      .pipe(map((response: any) => this.extractData(response)));
  }

  override delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/delete?id=${id}`).pipe(map(() => void 0));
  }

  getActiveFormats(): Observable<FormatoContableResponse[]> {
    return this.http.get<any>(`${this.baseUrl}/activos`).pipe(
      map((response: any) => {
        const content = response?.response?.content || response?.content || response?.data || [];
        return content as FormatoContableResponse[];
      }),
    );
  }

  getActiveFormatsByOperation(
    tipoOperacionConceptoId: number,
  ): Observable<FormatoContableResponse[]> {
    return this.http.getContent<FormatoContableResponse[]>(`${this.baseUrl}/por-operacion`, {
      tipoOperacionConceptoId: String(tipoOperacionConceptoId),
    });
  }

  override getPage(
    page = 0,
    size = 10,
    filters?: Record<string, any>,
  ): Observable<PageResponseFormatoContable> {
    return this.getPageResponseRaw(page, size, filters).pipe(
      map((response: any) => {
        const envelope = response?.response || response;
        const pageData = envelope?.content || {};
        const content = pageData.content || [];

        return {
          content: content,
          size: pageData.size || size,
          number: pageData.number || page,
          numberOfElements: pageData.numberOfElements || 0,
          totalElements: pageData.totalElements || 0,
          totalPages: pageData.totalPages || 0,
          empty: (content || []).length === 0,
          pageNumber: pageData.number || page,
          pageSize: pageData.size || size,
          first: (pageData.number || page) === 0,
          last: (pageData.number || page) === (pageData.totalPages || 0) - 1,
        } as PageResponseFormatoContable;
      }),
    );
  }

  getPaginatedFormats(
    page = 0,
    size = 10,
    searchTerm?: string,
    sortColumn?: string,
    sortDirection: 'ASC' | 'DESC' = 'ASC',
  ): Observable<PageResponseFormatoContable> {
    const filters = this.filterService.build(page, size, {
      search: searchTerm,
      fields: this.searchFields,
      operation: 'MATCH',
      linkOperator: 'OR',
      sort: sortColumn,
      direction: sortDirection,
    });

    return this.getPage(page, size, filters);
  }

  getFormatsWithFilters(
    page = 0,
    size = 10,
    filters?: {
      codigo?: string;
      nombre?: string;
      tipoOperacionConceptoId?: number;
      status?: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
    },
    sortColumn?: string,
    sortDirection: 'ASC' | 'DESC' = 'ASC',
  ): Observable<PageResponseFormatoContable> {
    const customFilters: Record<string, any> = {};

    if (filters?.codigo) {
      customFilters['codigo'] = filters.codigo;
    }
    if (filters?.nombre) {
      customFilters['nombre'] = filters.nombre;
    }
    if (filters?.tipoOperacionConceptoId) {
      customFilters['tipoOperacionConceptoId'] = filters.tipoOperacionConceptoId;
    }
    if (filters?.status) {
      customFilters['status'] = filters.status;
    }

    const sortParams = sortColumn ? { sortBy: [`${sortColumn},${sortDirection}`] } : {};

    const requestBody = {
      page,
      size,
      filters: customFilters,
      ...sortParams,
    };

    return this.http
      .postContent<PageResponseFormatoContable>(`${this.baseUrl}/page-response`, requestBody)
      .pipe(
        map((response: any) => {
          const data = response?.response?.content || response?.content || response;
          return {
            content: data.content || [],
            size: data.size || size,
            number: data.number || page,
            numberOfElements: data.numberOfElements || 0,
            totalElements: data.totalElements || 0,
            totalPages: data.totalPages || 0,
            empty: (data.content || []).length === 0,
            pageNumber: data.number || page,
            pageSize: data.size || size,
            first: (data.number || page) === 0,
            last: (data.number || page) === (data.totalPages || 0) - 1,
          } as PageResponseFormatoContable;
        }),
      );
  }

  private extractData(response: any): any {
    const data = response?.response?.content || response?.data || response;
    return data;
  }

  searchFormatos(searchTerm: string): Observable<FormatoContableResponse[]> {
    return this.getActiveFormats().pipe(
      map((formatos) =>
        formatos.filter(
          (f) =>
            f.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            f.nombre.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      ),
    );
  }

  aplicarFormato(request: AplicarFormatoRequest): Observable<AplicarFormatoResponse> {
    return this.http
      .post<AplicarFormatoResponse>(
        `${API_CONFIG.accounting.baseUrl}/causaciones/aplicar-formato`,
        request,
      )
      .pipe(
        map((response: any) => {
          const data = response?.response?.content || response?.data || response;
          return data as AplicarFormatoResponse;
        }),
      );
  }

  aplicarFormatoAsiento(request: AplicarFormatoAsientoRequest): Observable<AplicarFormatoResponse> {
    return this.http
      .post<AplicarFormatoResponse>(
        `${API_CONFIG.accounting.baseUrl}${API_CONFIG.accounting.endpoints.asientosContables}/formato-contable/aplicar`,
        request,
      )
      .pipe(
        map((response: any) => {
          const data = response?.response?.content || response?.data || response;
          return data as AplicarFormatoResponse;
        }),
      );
  }

  // En formato-contable.service.ts - Corregir el método getFormatsWithFiltersCustom
  getFormatsWithFiltersCustom(payload: any): Observable<PageResponseFormatoContable> {
    return this.http
      .postContent<PageResponseFormatoContable>(`${this.baseUrl}/page-response`, payload)
      .pipe(
        map((response: any) => {
          // La respuesta tiene: { length, metadata, content: { number, size, numberOfElements, totalElements, totalPages, content: [...] } }
          const data = response?.content || response?.response?.content || response;

          // Si data tiene un campo 'content' que es un array, usamos ese
          const contentArray = data?.content || [];
          const totalElements = data?.totalElements || contentArray.length || 0;
          const totalPages = data?.totalPages || 0;
          const number = data?.number || payload?.page || 0;
          const size = data?.size || payload?.size || 10;

          return {
            content: contentArray,
            size: size,
            number: number,
            numberOfElements: contentArray.length,
            totalElements: totalElements,
            totalPages: totalPages,
            empty: contentArray.length === 0,
            pageNumber: number,
            pageSize: size,
            first: number === 0,
            last: number === totalPages - 1,
          } as PageResponseFormatoContable;
        }),
      );
  }
}
